// Partner visits API (2026-10-06): see docs/superpowers/specs/2026-10-06-partner-visits-api-design.md

import { h } from 'preact';
import { Text } from 'preact-i18n';

// Tilleggsmodul (C) and signing/NDA (B✕) sidebar entries live in the product
// registries and are placed back at their exact original positions via
// other('<key>') / signing('<key>') below, so menu order is unchanged in the
// full build. The Dyrejournal-repo export stubs the registries with empty
// arrays and the lookups filter out cleanly.
import { sidebarItemsOther } from '../../product/menu-other';
import { sidebarItemsSigning } from '../../product/menu-signing';

const otherByKey = {};
sidebarItemsOther.forEach((item) => { otherByKey[item.key] = item; });
const other = key => otherByKey[key];

const signingByKey = {};
sidebarItemsSigning.forEach((item) => { signingByKey[item.key] = item; });
const signing = key => signingByKey[key];

export const SIDEBAR_CATEGORIES = [
    { key: 'navigation', label: '' },
    { key: 'dyrejournal', label: <Text id='sidebar.category-dyrejournal'>Dyrejournal</Text> },
    { key: 'shooting', label: <Text id='sidebar.category-shooting'>Shooting</Text> },
    { key: 'clubOperations', label: <Text id='sidebar.category-clubOperations'>Klubbdrift</Text> },
    { key: 'raceMushing', label: <Text id='sidebar.category-raceMushing'>Race / Mushing</Text> },
    { key: 'tools', label: <Text id='sidebar.category-tools'>Tools</Text> },
    { key: 'utility', label: '' },
    { key: 'custom', label: <Text id='sidebar.category-custom'>Custom Links</Text> },
];

export const SIDEBAR_ITEMS = [
    // Navigation
    { key: 'frontpage', category: 'navigation', icon: 'fa-solid fa-house', label: 'Frontpage',
      action: 'route', href: '/', keyShortCut: 'g f',
      isVisible: () => true },
    { key: 'calendar', category: 'navigation', icon: 'fa-regular fa-calendar', label: 'Calendar',
      action: 'route', href: '/calendar/', keyShortCut: 'g c',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.calendar && !opts.showDyrejournal) && user.isApproved },
    other('feed'),
    { key: 'shorts', category: 'navigation', icon: 'fa-duotone fa-rectangle-vertical', label: 'Shorts',
      action: 'route', href: '/feed/shorts',
      isVisible: ({ currentCustomer }) => currentCustomer?.menuItems?.news },
    other('my-photos'),
    other('marketplace'),
    other('courses'),
    other('elearning'),
    other('coffee'),
    other('f1'),

    // Dyrejournal
    { key: 'dyrejournal-contrast', category: 'dyrejournal', icon: 'fa-solid fa-circle-half-stroke',
      label: 'Contrast', action: 'drawer', drawer: 'dyrejournalContrast', drawerSide: 'right',
      isVisible: ({ currentCustomer, opts, user }) =>
          !!user.id && (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-sale', category: 'dyrejournal', icon: 'fa-duotone fa-user-doctor-hair', label: 'Sale',
      action: 'route', href: '/dyrejournal-sale/', keyShortCut: 'd s',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'all-visits', category: 'dyrejournal', icon: 'fa-duotone fa-list', label: 'All Visits',
      action: 'drawer', drawer: 'allVisits', drawerSide: 'right',
      drawerOptions: { hideAdmin: false, title: 'All visits' },
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-calendar', category: 'dyrejournal', icon: 'fa-regular fa-calendar', label: 'Calendar',
      action: 'route', href: '/calendar/', keyShortCut: 'g c',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'journals', category: 'dyrejournal', icon: 'fa-duotone fa-book', label: 'Journals',
      action: 'drawer', drawer: 'journals', drawerSide: 'right',
      drawerOptions: { hideAdmin: false, title: 'Journals' },
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'reports', category: 'dyrejournal', icon: 'fa-duotone fa-file-chart-column', label: 'Reports',
      action: 'drawer', drawer: 'reports', drawerSide: 'right',
      drawerOptions: { hideAdmin: false, title: 'Reports' },
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-clinic', category: 'dyrejournal', icon: 'fa-regular fa-hospital', label: 'Clinic',
      action: 'route', href: '/dyrejournal-clinic/', keyShortCut: 'd c',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-phone-consultations', category: 'dyrejournal', icon: 'fa-duotone fa-phone-volume', label: 'Phone Consultations',
      action: 'route', href: '/dyrejournal-phone-consultations/', keyShortCut: 'p c',
      isVisible: ({ currentCustomer, opts, user, featureFlags }) => featureFlags.phoneConsultations && (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-prescription-orders', category: 'dyrejournal', icon: 'fa-duotone fa-prescription', label: 'Prescription Orders',
      action: 'route', href: '/dyrejournal-prescription-orders/', keyShortCut: 'p o',
      isVisible: ({ currentCustomer, opts, user, featureFlags }) => featureFlags.onlinePrescriptionOrders && (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-payments', category: 'dyrejournal', icon: 'fa-regular fa-money-bill', label: 'Payments',
      action: 'route', href: '/dyrejournal-payments/', keyShortCut: 'd p',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-accounts', category: 'dyrejournal', icon: 'fa-duotone fa-list-ol', label: 'Chart of Accounts',
      action: 'route', href: '/dyrejournal-accounts/', keyShortCut: 'd a',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-lab', category: 'dyrejournal', icon: 'fa-solid fa-flask-vial', label: 'Lab Results',
      action: 'route', href: '/dyrejournal-lab-results/', keyShortCut: 'd l r',
      isVisible: ({ currentCustomer, opts, user, featureFlags }) => featureFlags.idexx && (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-idexx-mapping', isRoleLink: true, category: 'dyrejournal', icon: 'fa-duotone fa-link', label: 'IDEXX Mapping',
      action: 'route', href: '/dyrejournal-idexx-mapping/', keyShortCut: 'd l m',
      isVisible: ({ currentCustomer, opts, user, featureFlags }) => featureFlags.idexx && user.isCustomerAdmin && (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-kpi', category: 'dyrejournal', icon: 'fa-duotone fa-chart-line', label: 'KPI Dashboard',
      action: 'route', href: '/dyrejournal-kpi/', keyShortCut: 'd k',
      isVisible: ({ currentCustomer, opts, user, featureFlags }) => featureFlags.kpiDashboard && (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-sms', category: 'dyrejournal', icon: 'fa-regular fa-comment-sms', label: 'SMS List',
      action: 'route', href: '/dyrejournal-sms/', keyShortCut: 'd l s',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-sms-reminder-log', category: 'dyrejournal', icon: 'fa-regular fa-clock-rotate-left', label: 'Reminder Log',
      action: 'route', href: '/dyrejournal-sms-reminder-log', keyShortCut: 'd l r',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-no-shows', category: 'dyrejournal', icon: 'fa-duotone fa-person-circle-question', label: 'No-shows',
      action: 'route', href: '/dyrejournal-no-shows/',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-notifications', category: 'dyrejournal', icon: 'fa-duotone fa-bell', label: 'Innkallinger',
      action: 'route', href: '/notifications', keyShortCut: 'd n',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-mailsent', category: 'dyrejournal', icon: 'fa-regular fa-envelope', label: 'Mail Sent',
      action: 'route', href: '/dyrejournal-mailsent/', keyShortCut: 'd l m',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-files', category: 'dyrejournal', icon: 'fa-regular fa-files', label: 'Files',
      action: 'route', href: '/dyrejournal-files/', keyShortCut: 'd f',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-pending-sales', category: 'dyrejournal', icon: 'fa-duotone fa-cloud-arrow-up', label: 'Pending Sales',
      action: 'route', href: '/dyrejournal-pending-sales/',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'dyrejournal-contacts', category: 'dyrejournal', icon: 'fa-regular fa-address-book', label: 'Contacts',
      action: 'route', href: '/dyrejournal-contacts/', keyShortCut: 'd l c',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'visitors', category: 'dyrejournal', icon: 'fa-regular fa-users', label: 'Visitors',
      action: 'route', href: '/visitor/', keyShortCut: 'd l v',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'animals', category: 'dyrejournal', icon: 'fa-regular fa-paw', label: 'Animals',
      action: 'route', href: '/animal/', keyShortCut: 'd l a',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'text-templates', category: 'dyrejournal', icon: 'fa-solid fa-text-size', label: 'Text Templates',
      action: 'route', href: '/texttemplates', keyShortCut: 't p',
      isVisible: ({ currentCustomer, opts, user }) => (currentCustomer?.menuItems?.dyrejournal || opts.showDyrejournal) && user.isApproved },
    { key: 'text-templates-menu', category: 'dyrejournal', icon: 'fa-solid fa-text-size', label: 'Text Templates',
      action: 'route', href: '/texttemplates', keyShortCut: 't p',
      isVisible: ({ currentCustomer, user }) => currentCustomer?.menuItems?.textTemplates && user.isApproved },

    // Shooting
    other('shooting-convention'),
    other('terminliste'),
    other('shooting-rankings'),
    other('julekalender'),
    other('shooting-calculator'),
    other('shooting-trainer'),
    other('shooting-light'),
    other('ballistic-calculator'),
    other('lot-testing'),
    other('ammo-sales'),
    other('shooting-exam'),
    other('vapensoknad'),

    // Klubbdrift
    other('club-weapons'),
    other('club-ammo'),
    other('club-ranges'),
    other('club-keys'),
    other('club-roles'),
    other('club-dugnad'),
    other('club-advent-calendar'),

    // Race / Mushing
    other('race'),
    other('musher-ranking'),

    // Tools
    other('timetracker'),
    other('installations'),
    other('parts'),
    other('inspections'),
    { key: 'mcp-connections', category: 'tools', icon: 'fa-solid fa-plug', label: 'AI connections',
      action: 'route', href: '/mcp-connections',
      isVisible: ({ user, currentCustomer }) => user.isApproved && !currentCustomer?.menuItems?.hideAiConnections },
    { key: 'media', category: 'tools', icon: 'fa-regular fa-images', label: 'Media',
      action: 'route', href: '/media/', keyShortCut: 'm l',
      isVisible: ({ currentCustomer, user }) => currentCustomer?.menuItems?.media && user.isApproved },
    { key: 'sale-webshop', category: 'tools', icon: 'fa-regular fa-shopping-cart', label: 'Sale',
      action: 'route', href: '/dyrejournal-sale/', keyShortCut: 'd s',
      // Race admins handle the orders from their race shop, so they get the sales
      // list even when the domain customer has not enabled the webshop for everyone.
      isVisible: ({ currentCustomer, opts, user, hasRaceAdminAccess }) => (currentCustomer?.menuItems?.webshop || hasRaceAdminAccess) && !opts.showDyrejournal && user.isApproved },
    { key: 'webshop', category: 'tools', icon: 'fa-solid fa-box', label: 'Webshop',
      action: 'route', href: '/products', keyShortCut: 'p w',
      isVisible: ({ currentCustomer }) => currentCustomer?.menuItems?.webshop },
    { key: 'product-list', category: 'tools', icon: 'fa-solid fa-box', label: 'Products List',
      action: 'route', href: '/products/table', keyShortCut: 'p l',
      // Race admins manage the products sold in their race shop, so they get the
      // list even when the domain customer has not enabled it for everyone.
      isVisible: ({ currentCustomer, user, hasRaceAdminAccess }) => (currentCustomer?.menuItems?.productList || hasRaceAdminAccess) && user.isApproved },
    { key: 'qrcode-scanner', category: 'tools', icon: 'fa-solid fa-qrcode', label: 'QR Code Scanner',
      action: 'route', href: '/products/qrcodeScanner', keyShortCut: 'p s',
      isVisible: ({ currentCustomer, user }) => currentCustomer?.menuItems?.productList && user.isApproved },
    { key: 'barcode-scanner', category: 'tools', icon: 'fa-solid fa-barcode', label: 'Barcode Scanner',
      action: 'route', href: '/products/barcodeScanner', keyShortCut: 'p b',
      isVisible: ({ currentCustomer, user }) => currentCustomer?.menuItems?.productList && user.isApproved },
    { key: 'product-imports', category: 'tools', icon: 'fa-duotone fa-file-import', label: 'My imports',
      action: 'route', href: '/products/imports', keyShortCut: 'p i',
      isVisible: ({ currentCustomer, user }) => currentCustomer?.menuItems?.productList && user.isApproved },
    other('work-sharing'),
    other('tv-radio-producer'),
    other('carpenter-tools'),
    other('gps-tracking'),
    { key: 'ticket-scanner', category: 'tools', icon: 'fa-solid fa-qrcode', label: 'Ticket Scanner',
      action: 'route', href: '/ticketScanner/', keyShortCut: 't s',
      isVisible: ({ user }) => user.isTicketWorker && user.isApproved },

    // Utility
    other('newsletter-articles'),
    { key: 'help', category: 'utility', icon: 'fa-duotone fa-circle-question', label: 'Help',
      action: 'route', href: '/help/',
      isVisible: ({ user }) => !!user.id },
    { key: 'support', category: 'utility', icon: 'fa-duotone fa-life-ring', label: 'Support',
      action: 'route', href: '/support',
      isVisible: ({ user, currentCustomer }) => !!user.id && !currentCustomer?.menuItems?.hideSupport },
    { key: 'prepare-offline', category: 'utility', icon: 'fa-solid fa-cloud-arrow-down', label: 'Prepare for offline',
      action: 'func', funcName: 'runOfflinePrefetch',
      isVisible: ({ user, currentCustomer }) => !!user.isApproved && !currentCustomer?.menuItems?.hideOfflinePrepare },
    signing('signing-requests'),
    signing('signRequest'),
    { key: 'inbox', category: 'utility', icon: 'fa-duotone fa-inbox', label: 'Inbox',
      action: 'drawer', drawer: 'inbox', drawerSide: 'right',
      isVisible: ({ user, userStore }) => user.id && !userStore.isBlockedFromComments },
    { key: 'profile', category: 'utility', icon: 'fa-solid fa-user', label: 'My Page',
      action: 'route', href: '/users/profile', keyShortCut: 'm p',
      isVisible: ({ currentCustomer }) => currentCustomer?.menuItems?.mypage },
    { key: 'notifications-settings', category: 'utility', icon: 'fa-duotone fa-bell', label: 'Notifications',
      action: 'route', href: '/settings/notifications',
      isVisible: ({ user, currentCustomer }) => !!user && !!user.id && !currentCustomer?.menuItems?.hideNotifications },
    { key: 'notification-logs', category: 'tools', icon: 'fa-duotone fa-bell-on', label: 'Push log',
      action: 'route', href: '/notification-logs', keyShortCut: 'a n',
      isVisible: ({ user }) => !!user && !!user.isAdmin },
    other('terrain-model'),
    other('feed-results'),
    other('admin-race-tracker-calendar'),
    other('admin-elearning'),
    other('admin-pistolklubb'),
    { key: 'ai-feedback-review', isRoleLink: true, category: 'tools', icon: 'fa-duotone fa-flag', label: 'AI Feedback Review',
      action: 'route', href: '/admin/ai-feedback',
      isVisible: ({ user }) => !!user && (!!user.isAdmin || !!user.isSupport) },
    { key: 'api-key-usage', category: 'tools', icon: 'fa-solid fa-key', label: 'API key usage',
      action: 'route', href: '/admin/api-tokens',
      isVisible: ({ user }) => !!user && !!user.isAdmin },
    { key: 'oauth-clients', category: 'tools', icon: 'fa-solid fa-plug-circle-check', label: 'OAuth clients',
      action: 'route', href: '/admin/oauth-clients',
      isVisible: ({ user }) => !!user && !!user.isAdmin },
    { key: 'feature-usage', category: 'tools', icon: 'fa-solid fa-chart-column', label: 'Bruk av nettjenester',
      action: 'route', href: '/admin/feature-usage',
      isVisible: ({ user }) => !!user && !!user.isAdmin },
    { key: 'idexx-admin', category: 'tools', icon: 'fa-duotone fa-flask-vial', label: 'IDEXX admin',
      action: 'route', href: '/admin/idexx',
      isVisible: ({ user }) => !!user && !!user.isAdmin },
    { key: 'product-lists-admin', category: 'tools', icon: 'fa-duotone fa-book', label: 'Varelister',
      action: 'route', href: '/admin/product-lists',
      isVisible: ({ user }) => !!user && !!user.isAdmin },
    { key: 'pricelists-admin', category: 'tools', icon: 'fa-duotone fa-tags', label: 'Prislister',
      action: 'route', href: '/pricelists',
      isVisible: ({ user }) => !!user && !!user.isAdmin },
    other('admin-checklists'),
    other('newsletter-admin'),
    other('refresh-scenes'),
    { key: 'lockScreen', category: 'utility', icon: 'fa-duotone fa-lock', label: 'Lock screen',
      action: 'func', funcName: 'lockScreen', keyShortCut: 'g l',
      isVisible: ({ currentCustomer, user }) => !!user?.id && !!currentCustomer?.featureFlags?.lockScreen?.enabled },
    { key: 'setPin', category: 'utility', icon: 'fa-duotone fa-key', label: 'Change PIN',
      action: 'route', href: '/profile/set-pin',
      isVisible: ({ currentCustomer, user }) => !!user?.id && !!currentCustomer?.featureFlags?.lockScreen?.enabled },
    { key: 'reload', category: 'utility', icon: 'fa-solid fa-arrows-rotate', label: 'Reload',
      action: 'func', funcName: 'reload', keyShortCut: 'shift+r',
      isVisible: () => true },
    { key: 'logout', category: 'utility', icon: 'fa-solid fa-right-from-bracket', label: 'Logout',
      action: 'route', href: '/logout', keyShortCut: 'l o',
      isVisible: () => true },
].filter(Boolean);

export function getVisibleItems(context) {
    // Context with the global admin flag stripped, used to detect items that
    // are visible only because the user is an admin. Those get marked
    // adminOnly and rendered in red, matching the hamburger menu. Items with
    // isRoleLink are unlocked by a role (club admin, range officer, support...)
    // and render amber instead, never red, matching menu/list.js.
    const nonAdminContext = {
        ...context,
        isAdmin: false,
        user: { ...context.user, isAdmin: false },
    };
    return SIDEBAR_ITEMS.filter(item => {
        try {
            return item.isVisible(context);
        } catch (e) {
            return false;
        }
    }).map(item => {
        let visibleWithoutAdmin;
        try {
            visibleWithoutAdmin = item.isVisible(nonAdminContext);
        } catch (e) {
            visibleWithoutAdmin = false;
        }
        return (visibleWithoutAdmin || item.isRoleLink) ? item : { ...item, adminOnly: true };
    });
}

export function mergeWithConfig(visibleItems, sidebarConfig) {
    if (!sidebarConfig || !sidebarConfig.items || sidebarConfig.items.length === 0) {
        return visibleItems.map((item, idx) => ({ ...item, order: idx, visible: true }));
    }

    const configByKey = {};
    sidebarConfig.items.forEach(ci => {
        if (ci.type === 'default') {
            configByKey[ci.key] = ci;
        }
    });

    const merged = visibleItems.map((item, idx) => {
        const override = configByKey[item.key];
        return {
            ...item,
            order: override != null ? override.order : idx + 1000,
            visible: override != null ? override.visible : true,
        };
    });

    const customItems = (sidebarConfig.items || [])
        .filter(ci => ci.type === 'custom' && ci.visible !== false)
        .map(ci => ({
            key: `custom-${ci.order}-${ci.text}`,
            category: 'custom',
            icon: ci.icon || 'fa-solid fa-link',
            label: ci.text || 'Custom Link',
            action: 'route',
            href: ci.href || '/',
            order: ci.order,
            visible: ci.visible !== false,
            isCustom: true,
        }));

    return [...merged, ...customItems]
        .filter(item => item.visible)
        .sort((a, b) => a.order - b.order);
}
